/**
 * PlanAndRunView – a one-glance view of a `<platform>_plan_and_run` call in the live debug panel.
 *
 * Input: the request as text and values as a table. Output: status first, then goal and note as
 * labelled text, the writes as method / path / body rows, the calls nested under their step, and
 * values as a table. Threads from before the `values` rename carry known_ids / known instead; those
 * are shown the same way. Anything else the tool returned falls through to StructuredValue.
 *
 * When the output carries `debug_trace` (action_library/api_agent_trace.py: every HTTP request the
 * call made, never shown to the model), the calls are those requests instead, each one expanding to
 * the query, body, errors and response it had.
 */

import React, { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { StructuredValue, unpackJsonStrings, type StructuredView } from "./StructuredValue";

export const PLAN_AND_RUN_TOOL = /_plan_and_run$/;

const COLORS = {
  bg: "#16181d",
  border: "#2c313a",
  text: "#e6edf3",
  muted: "#8b949e",
  key: "#79c0ff",
  ok: "#7ee787",
  warn: "#e3b341",
  error: "#ff7b72",
  write: "#d2a8ff",
};

const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

const STATUS_COLOR: Record<string, string> = {
  done: COLORS.ok,
  ready_to_write: COLORS.write,
  needs_input: COLORS.warn,
  failed: COLORS.error,
};

const METHOD_COLOR: Record<string, string> = {
  GET: COLORS.ok,
  FIND: COLORS.key,
  POST: COLORS.ok,
  PATCH: COLORS.warn,
  PUT: COLORS.warn,
  DELETE: COLORS.error,
};

const INPUT_SHOWN = new Set(["request", "values", "known_ids"]);
const OUTPUT_SHOWN = new Set(["status", "goal", "note", "values", "known", "calls", "writes", "replans", "reason", "question", "debug_trace"]);

type Obj = Record<string, unknown>;

const isObj = (value: unknown): value is Obj => !!value && typeof value === "object" && !Array.isArray(value);
const present = (value: unknown) =>
  value !== null && value !== undefined && value !== "" && !(typeof value === "object" && Object.keys(value as Obj).length === 0);

function asObj(value: unknown): Obj | null {
  const unpacked = unpackJsonStrings(value);
  return isObj(unpacked) ? unpacked : null;
}

function scalar(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/** A call string such as "GET /V1.0/Tickets/query" split into its method and path. */
export function splitCall(call: unknown): { method: string; path: string } {
  const text = String(call ?? "");
  const match = /^([A-Z]+)\s+(.*)$/.exec(text);
  return match ? { method: match[1], path: match[2] } : { method: "", path: text };
}

/** Calls grouped by plan step, in the order the steps first ran. */
export function groupCallsByStep(calls: unknown): { step: string; calls: Obj[] }[] {
  if (!Array.isArray(calls)) return [];
  const groups: { step: string; calls: Obj[] }[] = [];
  calls.forEach((call) => {
    if (!isObj(call)) return;
    const step = call.step == null ? "?" : String(call.step);
    const group = groups.find((g) => g.step === step) ?? groups[groups.push({ step, calls: [] }) - 1];
    group.calls.push(call);
  });
  return groups;
}

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: 10.5, letterSpacing: 0.6, textTransform: "uppercase", color: COLORS.muted, mt: 1 }}>{children}</Typography>
);

const Text: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: 12.5, color: COLORS.text, whiteSpace: "pre-wrap", wordBreak: "break-word", mt: 0.25 }}>{children}</Typography>
);

const Method: React.FC<{ method: string }> = ({ method }) => (
  <Box component="span" sx={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, color: METHOD_COLOR[method] ?? COLORS.muted, minWidth: 44, display: "inline-block" }}>
    {method}
  </Box>
);

const boxSx = { mt: 0.5, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 1, overflow: "hidden" };
const cellSx = { px: 1, py: 0.4, fontSize: 11.5, fontFamily: MONO, borderTop: `1px solid ${COLORS.border}`, verticalAlign: "top", wordBreak: "break-word" as const };

/** Two-column key / value table. */
export const KeyValueTable: React.FC<{ value: unknown }> = ({ value }) => {
  const obj = asObj(value);
  if (!obj || Object.keys(obj).length === 0) return <Text>—</Text>;
  return (
    <Box component="table" sx={{ ...boxSx, width: "100%", borderCollapse: "collapse", "& tr:first-of-type td": { borderTop: "none" } }}>
      <tbody>
        {Object.entries(obj).map(([k, v]) => (
          <tr key={k}>
            <Box component="td" sx={{ ...cellSx, color: COLORS.key, whiteSpace: "nowrap", width: "1%" }}>{k}</Box>
            <Box component="td" sx={{ ...cellSx, color: COLORS.text }}>{scalar(v)}</Box>
          </tr>
        ))}
      </tbody>
    </Box>
  );
};

const CallsByStep: React.FC<{ calls: unknown }> = ({ calls }) => {
  const groups = groupCallsByStep(calls);
  if (groups.length === 0) return <Text>—</Text>;
  return (
    <Box sx={boxSx}>
      {groups.map((group, gi) => (
        <Box key={group.step} sx={{ borderTop: gi ? `1px solid ${COLORS.border}` : "none", py: 0.4 }}>
          <Typography sx={{ px: 1, fontSize: 11, fontWeight: 600, color: COLORS.muted }}>
            Step {group.step} · {group.calls.length} call{group.calls.length === 1 ? "" : "s"}
          </Typography>
          {group.calls.map((call, ci) => {
            const { method, path } = splitCall(call.call);
            const status = call.status;
            const bad = typeof status === "number" ? status >= 400 : status === "error" || status === false;
            return (
              <Box key={ci} sx={{ display: "flex", gap: 1, alignItems: "baseline", pl: 2.5, pr: 1, fontFamily: MONO, fontSize: 11.5 }}>
                <Method method={method} />
                <Box component="span" sx={{ flex: 1, minWidth: 0, color: COLORS.text, wordBreak: "break-all" }}>{path}</Box>
                {status != null && status !== "" && (
                  <Box component="span" sx={{ color: bad ? COLORS.error : COLORS.muted }}>{String(status)}</Box>
                )}
                {call.ms != null && <Box component="span" sx={{ color: COLORS.muted, whiteSpace: "nowrap" }}>{String(call.ms)}ms</Box>}
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

/** The HTTP requests in the output's debug_trace, or null when it has none (older rows, other tools). */
export function traceHttp(output: Obj | null): Obj[] | null {
  const trace = output && isObj(output.debug_trace) ? output.debug_trace : null;
  return trace && Array.isArray(trace.http) ? trace.http.filter(isObj) : null;
}

/** Requests grouped by attempt and step, in the order each group first appears. For plan_and_run a step is a
 * plan step and a request outside any (the planner reading picklists) is "Planning"; for write_api a step is a
 * write key and one outside any write is "Before sending". */
export function groupHttpByStep(http: Obj[], kind: string = "plan_and_run"): { label: string; calls: Obj[] }[] {
  const writes = kind === "write_api";
  const multipleAttempts = new Set(http.map((call) => String(call.attempt ?? 1))).size > 1;
  const groups: { key: string; label: string; calls: Obj[] }[] = [];
  http.forEach((call) => {
    const attempt = String(call.attempt ?? 1);
    const step = call.step == null ? (writes ? "Before sending" : "Planning") : writes ? `Write #${call.step}` : `Step ${call.step}`;
    const key = `${attempt}:${step}`;
    let group = groups.find((g) => g.key === key);
    if (!group) {
      group = { key, label: multipleAttempts ? `Attempt ${attempt} · ${step}` : step, calls: [] };
      groups.push(group);
    }
    group.calls.push(call);
  });
  return groups.map(({ label, calls }) => ({ label, calls }));
}

const HttpHalf: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ mt: 0.75, pl: 1, borderLeft: `2px solid ${title === "Request" ? COLORS.key : COLORS.ok}55` }}>
    <Typography sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: title === "Request" ? COLORS.key : COLORS.ok }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const HttpCall: React.FC<{ call: Obj; view: StructuredView; preSx: object }> = ({ call, view, preSx }) => {
  const [open, setOpen] = useState(false);
  const method = String(call.method ?? "");
  const notSent = call.sent === false;
  const bad = notSent || call.ok === false;
  return (
    <Box>
      <Box
        role="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        sx={{ display: "flex", gap: 1, alignItems: "baseline", pl: 2.5, pr: 1, fontFamily: MONO, fontSize: 11.5, cursor: "pointer", "&:hover": { background: "#ffffff08" } }}
      >
        <Box component="span" sx={{ color: COLORS.muted, width: 10 }}>{open ? "▾" : "▸"}</Box>
        <Method method={method} />
        <Box component="span" sx={{ flex: 1, minWidth: 0, color: COLORS.text, wordBreak: "break-all" }}>
          {scalar(call.path)}
          {present(call.item) && <Box component="span" sx={{ color: COLORS.muted }}> · {scalar(call.item)}</Box>}
        </Box>
        <Box component="span" sx={{ color: bad ? COLORS.error : COLORS.muted }}>{notSent ? "not sent" : scalar(call.status)}</Box>
        {call.ms != null && <Box component="span" sx={{ color: COLORS.muted, whiteSpace: "nowrap" }}>{String(call.ms)}ms</Box>}
      </Box>
      {open && (
        <Box sx={{ pl: 4.5, pr: 1, pb: 0.75 }}>
          <HttpHalf title="Request">
            <KeyValueTable
              value={{
                method,
                url: call.url ?? call.path,
                ...(present(call.operation) ? { operation: call.operation } : {}),
                ...(present(call.item) ? { item: call.item } : {}),
              }}
            />
            {present(call.query) && (
              <>
                <Label>query</Label>
                <StructuredValue value={call.query} view={view} preSx={preSx} />
              </>
            )}
            {present(call.body) && (
              <>
                <Label>body</Label>
                <StructuredValue value={call.body} view={view} preSx={preSx} />
              </>
            )}
          </HttpHalf>
          <HttpHalf title="Response">
            <KeyValueTable
              value={{
                status: notSent ? "not sent (refused before sending)" : call.status ?? "no response",
                ok: call.ok,
                ...(call.ms != null ? { time: `${call.ms}ms` } : {}),
              }}
            />
            {present(call.errors) && (
              <>
                <Label>errors</Label>
                <StructuredValue value={call.errors} view={view} preSx={preSx} />
              </>
            )}
            {present(call.response) && (
              <>
                <Label>body</Label>
                <StructuredValue value={call.response} view={view} preSx={preSx} />
              </>
            )}
          </HttpHalf>
        </Box>
      )}
    </Box>
  );
};

const HttpCallsByStep: React.FC<{ http: Obj[]; kind?: string; view: StructuredView; preSx: object }> = ({ http, kind, view, preSx }) => {
  const groups = groupHttpByStep(http, kind);
  if (groups.length === 0) return <Text>—</Text>;
  return (
    <Box sx={boxSx}>
      {groups.map((group, gi) => (
        <Box key={group.label} sx={{ borderTop: gi ? `1px solid ${COLORS.border}` : "none", py: 0.4 }}>
          <Typography sx={{ px: 1, fontSize: 11, fontWeight: 600, color: COLORS.muted }}>
            {group.label} · {group.calls.length} request{group.calls.length === 1 ? "" : "s"}
          </Typography>
          {group.calls.map((call, ci) => (
            <HttpCall key={ci} call={call} view={view} preSx={preSx} />
          ))}
        </Box>
      ))}
    </Box>
  );
};

const NoteList: React.FC<{ label: string; color: string; value: unknown }> = ({ label, color, value }) => {
  if (!present(value)) return null;
  const items = Array.isArray(value)
    ? value.map((v) => scalar(v))
    : isObj(value)
      ? Object.entries(value).map(([k, v]) => `#${k}: ${Array.isArray(v) ? v.join(", ") : scalar(v)}`)
      : [scalar(value)];
  return (
    <Box sx={{ px: 1, pb: 0.5 }}>
      {items.map((item, i) => (
        <Typography key={i} sx={{ fontSize: 11.5, color }}>
          {label}: {item}
        </Typography>
      ))}
    </Box>
  );
};

const WriteGroups: React.FC<{ writes: unknown }> = ({ writes }) => {
  const groups = Array.isArray(writes) ? writes.filter(isObj) : [];
  if (groups.length === 0) return <Text>—</Text>;
  return (
    <>
      {groups.map((group, gi) => {
        const entries = isObj(group.writes) ? Object.entries(group.writes).filter(([, w]) => isObj(w)) : [];
        return (
          <Box key={gi} sx={boxSx}>
            {present(group.summary) && (
              <Typography sx={{ px: 1, py: 0.5, fontSize: 12, fontWeight: 600, color: COLORS.text }}>{scalar(group.summary)}</Typography>
            )}
            {entries.map(([key, raw]) => {
              const write = raw as Obj;
              const body = asObj(write.body);
              return (
                <Box key={key} sx={{ borderTop: `1px solid ${COLORS.border}`, px: 1, py: 0.5 }}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "baseline", fontFamily: MONO, fontSize: 11.5 }}>
                    <Box component="span" sx={{ color: COLORS.muted }}>#{key}</Box>
                    <Method method={String(write.method ?? "")} />
                    <Box component="span" sx={{ flex: 1, minWidth: 0, color: COLORS.text, wordBreak: "break-all" }}>{scalar(write.path)}</Box>
                    {present(write.id_as) && <Box component="span" sx={{ color: COLORS.muted }}>→ {`{${scalar(write.id_as)}}`}</Box>}
                  </Box>
                  {body && Object.keys(body).length > 0 && <KeyValueTable value={body} />}
                </Box>
              );
            })}
            <NoteList label="problem" color={COLORS.error} value={group.problems} />
            <NoteList label="warning" color={COLORS.warn} value={group.warnings} />
            <NoteList label="skipped" color={COLORS.muted} value={group.skipped} />
            <NoteList label="dropped" color={COLORS.muted} value={group.dropped} />
          </Box>
        );
      })}
    </>
  );
};

const Rest: React.FC<{ obj: Obj; shown: Set<string>; view: StructuredView; preSx: object }> = ({ obj, shown, view, preSx }) => (
  <>
    {Object.entries(obj)
      .filter(([k, v]) => !shown.has(k) && present(v))
      .map(([k, v]) => (
        <Box key={k}>
          <Label>{k.replace(/_/g, " ")}</Label>
          {typeof v === "string" ? <Text>{v}</Text> : <StructuredValue value={v} view={view} preSx={preSx} />}
        </Box>
      ))}
  </>
);

/** True when the row's input or output can be read as a plan (the output may be truncated text). */
export function isPlanAndRun(toolName: string | null | undefined): boolean {
  return PLAN_AND_RUN_TOOL.test(toolName || "");
}

export const PlanInput: React.FC<{ value: unknown; view: StructuredView; preSx: object }> = ({ value, view, preSx }) => {
  const input = asObj(value);
  if (!input) return <StructuredValue value={value} view={view} preSx={preSx} />;
  const values = input.values ?? input.known_ids;
  return (
    <>
      <Label>Request</Label>
      <Text>{scalar(input.request)}</Text>
      {present(values) && (
        <>
          <Label>Values</Label>
          <KeyValueTable value={values} />
        </>
      )}
      <Rest obj={input} shown={INPUT_SHOWN} view={view} preSx={preSx} />
    </>
  );
};

export const PlanOutput: React.FC<{ value: unknown; view: StructuredView; preSx: object }> = ({ value, view, preSx }) => {
  const output = asObj(value);
  if (!output || !("status" in output)) return <StructuredValue value={value} view={view} preSx={preSx} />;
  const status = scalar(output.status);
  const color = STATUS_COLOR[status] ?? COLORS.muted;
  const callCount = Array.isArray(output.calls) ? output.calls.length : 0;
  const question = isObj(output.question) ? output.question : null;
  const http = traceHttp(output);
  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
        <Box
          component="span"
          sx={{ px: 1, py: "2px", borderRadius: 0.75, fontSize: 12, fontWeight: 700, color, border: `1px solid ${color}88`, textTransform: "uppercase", fontFamily: MONO }}
        >
          {status}
        </Box>
        <Typography sx={{ fontSize: 11, color: COLORS.muted }}>
          {callCount} call{callCount === 1 ? "" : "s"}
          {http ? ` · ${http.length} request${http.length === 1 ? "" : "s"}` : ""}
          {Number(output.replans) > 0 ? ` · ${output.replans} replan${Number(output.replans) === 1 ? "" : "s"}` : ""}
        </Typography>
      </Box>
      {present(output.goal) && (
        <>
          <Label>Goal</Label>
          <Text>{scalar(output.goal)}</Text>
        </>
      )}
      {present(output.note) && (
        <>
          <Label>Note</Label>
          <Text>{scalar(output.note)}</Text>
        </>
      )}
      {present(output.reason) && (
        <>
          <Label>Reason</Label>
          <Text>{scalar(output.reason)}</Text>
        </>
      )}
      {present(output.question) && (
        <>
          <Label>Question</Label>
          {question ? <KeyValueTable value={question} /> : <Text>{scalar(output.question)}</Text>}
        </>
      )}
      {present(output.writes) && (
        <>
          <Label>Writes</Label>
          <WriteGroups writes={output.writes} />
        </>
      )}
      {http ? (
        <>
          <Label>API calls (click one for its request and response)</Label>
          <HttpCallsByStep http={http} view={view} preSx={preSx} />
        </>
      ) : (
        <>
          <Label>Calls</Label>
          <CallsByStep calls={output.calls} />
        </>
      )}
      <Label>Values</Label>
      <KeyValueTable value={output.values ?? output.known} />
      <Rest obj={output} shown={OUTPUT_SHOWN} view={view} preSx={preSx} />
    </>
  );
};

// overview lines: one line per collapsed row in the debug panel's list

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** "done · 1 replan · 4 steps · 6 requests (1 failed) · 2 results · 1 write group" for a plan_and_run output, or null. */
export function planAndRunSummary(value: unknown): string | null {
  const output = asObj(value);
  if (!output || !("status" in output)) return null;
  const http = traceHttp(output);
  const failed = http ? http.filter((call) => call.sent === false || call.ok === false).length : 0;
  const count = (key: string) => (Array.isArray(output[key]) ? (output[key] as unknown[]).length : 0);
  return [
    scalar(output.status),
    plural(Number(output.replans) || 0, "replan"),
    plural(count("calls"), "step"),
    http ? `${plural(http.length, "request")}${failed ? ` (${failed} failed)` : ""}` : "",
    plural(count("results"), "result"),
    count("writes") ? plural(count("writes"), "write group") : "",
  ]
    .filter(Boolean)
    .join(" · ");
}

/** Each write of a write_api call with what happened to it, in send order. */
export function writeOutcomes(input: unknown, value: unknown): { key: string; method: string; path: string; outcome: string; bad: boolean }[] {
  const args = asObj(input);
  const output = asObj(value) ?? {};
  const writes = args && isObj(args.writes) ? (args.writes as Obj) : {};
  const done = isObj(output.done) ? (output.done as Obj) : {};
  const keys = Object.keys(writes).sort((a, b) => (Number(a) || 0) - (Number(b) || 0) || a.localeCompare(b));
  return keys.map((key) => {
    const write = isObj(writes[key]) ? (writes[key] as Obj) : {};
    const result = isObj(done[key]) ? (done[key] as Obj) : null;
    let outcome = "not reached";
    let bad = false;
    if (output.failed === key) {
      outcome = output.status != null ? `failed · HTTP ${scalar(output.status)}` : "failed · not sent";
      bad = true;
    } else if (result?.already_done) outcome = "already done";
    else if (result && "skipped" in result) outcome = `skipped · ${scalar(result.skipped)}`;
    else if (result) {
      outcome = `sent · ${scalar(result.status)}`;
      if (present(result.verified)) outcome += ` · ${scalar(result.verified)}`;
      bad = result.verified === "mismatch";
    }
    return { key, method: String(write.method ?? ""), path: scalar(write.path), outcome, bad };
  });
}

/** "sent · 2 writes · 2 sent · 5 requests" or "failed at #2 · 2 writes · 1 sent · 3 requests" for a write_api row, or null. */
export function writeApiSummary(input: unknown, value: unknown): string | null {
  const output = asObj(value);
  if (!output || !("done" in output || "ok" in output)) return null;
  const outcomes = writeOutcomes(input, output);
  const sent = outcomes.filter((w) => w.outcome.startsWith("sent")).length;
  const skipped = outcomes.filter((w) => w.outcome.startsWith("skipped") || w.outcome === "already done").length;
  const http = traceHttp(output);
  return [
    output.ok === false ? (present(output.failed) ? `failed at #${scalar(output.failed)}` : "failed") : "sent",
    plural(outcomes.length, "write"),
    `${sent} sent`,
    skipped ? `${skipped} skipped` : "",
    http ? plural(http.filter((call) => call.sent !== false).length, "request") : "",
  ]
    .filter(Boolean)
    .join(" · ");
}

// write_api

export const WRITE_API_TOOL = /_write_api$/;

export function isWriteApi(toolName: string | null | undefined): boolean {
  return WRITE_API_TOOL.test(toolName || "");
}

const WRITE_INPUT_SHOWN = new Set(["summary", "writes", "completed"]);
const WRITE_OUTPUT_SHOWN = new Set(["ok", "status", "errors", "error", "done", "failed", "remaining", "debug_trace", "response"]);
const WRITE_FIELDS_SHOWN = new Set(["method", "path", "body"]);
const RESULT_SHOWN = new Set(["status", "verified", "already_done", "skipped", "produced", "response", "stored", "mismatches"]);

export type WriteTab = "input" | "output" | "requests";

/** One write of a write_api call: what was asked (input), what came back (output) and the HTTP requests made for it. */
export function writeExchanges(
  input: unknown,
  value: unknown,
): { key: string; method: string; path: string; outcome: string; bad: boolean; write: Obj; result: Obj | null; failed: boolean; http: Obj[] }[] {
  const args = asObj(input);
  const output = asObj(value) ?? {};
  const writes = args && isObj(args.writes) ? (args.writes as Obj) : {};
  const done = isObj(output.done) ? (output.done as Obj) : {};
  const http = traceHttp(output) ?? [];
  return writeOutcomes(input, value).map((w) => ({
    ...w,
    write: isObj(writes[w.key]) ? (writes[w.key] as Obj) : {},
    result: isObj(done[w.key]) ? (done[w.key] as Obj) : null,
    failed: output.failed === w.key,
    http: http.filter((call) => call.step != null && String(call.step) === w.key),
  }));
}

const TabBar: React.FC<{ tab: WriteTab; tabs: [WriteTab, string][]; onTab: (tab: WriteTab) => void }> = ({ tab, tabs, onTab }) => (
  <Box role="tablist" sx={{ display: "flex", gap: 0.25, borderBottom: `1px solid ${COLORS.border}`, mt: 0.5 }}>
    {tabs.map(([id, label]) => (
      <Box
        key={id}
        role="tab"
        aria-selected={tab === id}
        onClick={() => onTab(id)}
        sx={{
          px: 1,
          py: 0.25,
          fontSize: 11,
          cursor: "pointer",
          color: tab === id ? COLORS.text : COLORS.muted,
          borderBottom: `2px solid ${tab === id ? COLORS.key : "transparent"}`,
          mb: "-1px",
          "&:hover": { color: COLORS.text },
        }}
      >
        {label}
      </Box>
    ))}
  </Box>
);

const Field: React.FC<{ label: string; value: unknown; view: StructuredView; preSx: object; table?: boolean }> = ({ label, value, view, preSx, table }) =>
  present(value) ? (
    <>
      <Label>{label}</Label>
      {table && asObj(value) ? <KeyValueTable value={value} /> : <StructuredValue value={value} view={view} preSx={preSx} />}
    </>
  ) : null;

type Exchange = ReturnType<typeof writeExchanges>[number];

const WriteExchangeRow: React.FC<{
  exchange: Exchange;
  first: boolean;
  open: boolean;
  onToggle: () => void;
  tab: WriteTab;
  onTab: (tab: WriteTab) => void;
  output: Obj | null;
  completed: unknown;
  view: StructuredView;
  preSx: object;
}> = ({ exchange, first, open, onToggle, tab, onTab, output, completed, view, preSx }) => {
  const { key, method, path, outcome, bad, write, result, failed, http } = exchange;
  const extra = Object.fromEntries(Object.entries(write).filter(([k, v]) => !WRITE_FIELDS_SHOWN.has(k) && present(v)));
  const tabs: [WriteTab, string][] = [
    ["input", "Input"],
    ["output", "Output"],
  ];
  if (http.length) tabs.push(["requests", `Requests (${http.length})`]);
  const shown = tab === "requests" && !http.length ? "output" : tab;
  const outcomeColor = bad ? COLORS.error : outcome.startsWith("sent") ? COLORS.ok : COLORS.muted;
  return (
    <Box sx={{ borderTop: first ? "none" : `1px solid ${COLORS.border}` }}>
      <Box
        role="button"
        aria-expanded={open}
        onClick={onToggle}
        sx={{ display: "flex", gap: 1, alignItems: "baseline", px: 1, py: 0.4, fontFamily: MONO, fontSize: 11.5, cursor: "pointer", "&:hover": { background: "#ffffff08" } }}
      >
        <Box component="span" sx={{ color: COLORS.muted, width: 10 }}>{open ? "▾" : "▸"}</Box>
        <Box component="span" sx={{ color: COLORS.muted }}>#{key}</Box>
        <Method method={method} />
        <Box component="span" sx={{ flex: 1, minWidth: 0, color: COLORS.text, wordBreak: "break-all" }}>{path}</Box>
        <Box component="span" sx={{ color: outcomeColor, whiteSpace: "nowrap" }}>{outcome}</Box>
      </Box>
      {open && (
        <Box sx={{ px: 1, pb: 0.75 }}>
          <TabBar tab={shown} tabs={tabs} onTab={onTab} />
          {shown === "input" && (
            <>
              <Label>body</Label>
              <KeyValueTable value={write.body} />
              {Object.keys(extra).length > 0 && (
                <>
                  <Label>options</Label>
                  <KeyValueTable value={extra} />
                </>
              )}
              <Field label="already completed (retry)" value={completed} view={view} preSx={preSx} />
            </>
          )}
          {shown === "output" && (
            <>
              {!output ? (
                <Text>No output yet.</Text>
              ) : !result && !failed ? (
                <Text>{outcome === "not reached" ? "Not sent: an earlier write stopped the call." : outcome}</Text>
              ) : (
                <>
                  <KeyValueTable
                    value={{
                      outcome,
                      ...(failed ? { status: output.status ?? "not sent" } : present(result?.status) ? { status: result?.status } : {}),
                      ...(present(result?.verified) ? { verified: result?.verified } : {}),
                      ...(result?.already_done ? { already_done: true } : {}),
                      ...(present(result?.skipped) ? { skipped: result?.skipped } : {}),
                    }}
                  />
                  {failed && present(output.errors) && (
                    <>
                      <Label>errors</Label>
                      {(Array.isArray(output.errors) ? output.errors : [output.errors]).map((e, i) => (
                        <Typography key={i} sx={{ fontSize: 12, color: COLORS.error, whiteSpace: "pre-wrap", mt: 0.25 }}>
                          {scalar(e)}
                        </Typography>
                      ))}
                    </>
                  )}
                  <Field label="ids produced" value={result?.produced} view={view} preSx={preSx} table />
                  <Field label="mismatches" value={result?.mismatches} view={view} preSx={preSx} table />
                  <Field label="response" value={failed ? output.response : result?.response} view={view} preSx={preSx} />
                  <Field label="read back" value={result?.stored} view={view} preSx={preSx} table />
                  {result && <Rest obj={result} shown={RESULT_SHOWN} view={view} preSx={preSx} />}
                </>
              )}
            </>
          )}
          {shown === "requests" && (
            <Box sx={{ ...boxSx, py: 0.4 }}>
              {http.map((call, ci) => (
                <HttpCall key={ci} call={call} view={view} preSx={preSx} />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

/**
 * A write_api call as one list of writes, DevTools-style: each row is a write with what happened to it, and opening
 * it shows that write's Input (body), Output (status, verification, ids, response) and the HTTP requests it made.
 */
export const WriteApiView: React.FC<{ input: unknown; value: unknown; view: StructuredView; preSx: object }> = ({ input, value, view, preSx }) => {
  const args = asObj(input);
  const parsed = asObj(value);
  const output = parsed && ("done" in parsed || "ok" in parsed) ? parsed : null;
  const exchanges = writeExchanges(input, output);
  const [tab, setTab] = useState<WriteTab>("input");
  const [opened, setOpened] = useState<Record<string, boolean>>({});
  if (!args || !isObj(args.writes)) {
    return (
      <>
        <Field label="Input" value={input} view={view} preSx={preSx} />
        <Field label="Output" value={value} view={view} preSx={preSx} />
      </>
    );
  }
  const isOpen = (e: Exchange) => opened[e.key] ?? (e.failed || exchanges.length === 1);
  const completed = isObj(args.completed) ? (args.completed as Obj) : {};
  const keys = new Set(exchanges.map((e) => e.key));
  const otherHttp = (traceHttp(output) ?? []).filter((call) => call.step == null || !keys.has(String(call.step)));
  const kind = output && isObj(output.debug_trace) ? String(output.debug_trace.kind ?? "write_api") : "write_api";
  const ok = output ? output.ok !== false : null;
  const color = ok === false ? COLORS.error : COLORS.ok;
  return (
    <>
      {output && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
          <Box
            component="span"
            sx={{ px: 1, py: "2px", borderRadius: 0.75, fontSize: 12, fontWeight: 700, color, border: `1px solid ${color}88`, textTransform: "uppercase", fontFamily: MONO }}
          >
            {ok ? "sent" : "failed"}
          </Box>
          <Typography sx={{ fontSize: 11, color: COLORS.muted }}>{writeApiSummary(input, output)}</Typography>
        </Box>
      )}
      {present(args.summary) && <Text>{scalar(args.summary)}</Text>}
      {output && !output.failed && present(output.errors) && (
        <>
          <Label>Errors</Label>
          {(Array.isArray(output.errors) ? output.errors : [output.errors]).map((e, i) => (
            <Typography key={i} sx={{ fontSize: 12, color: COLORS.error, whiteSpace: "pre-wrap", mt: 0.25 }}>
              {scalar(e)}
            </Typography>
          ))}
        </>
      )}
      <Label>Writes (click one for its input and output)</Label>
      <Box sx={boxSx}>
        {exchanges.map((exchange, i) => (
          <WriteExchangeRow
            key={exchange.key}
            exchange={exchange}
            first={i === 0}
            open={isOpen(exchange)}
            onToggle={() => setOpened({ ...opened, [exchange.key]: !isOpen(exchange) })}
            tab={tab}
            onTab={setTab}
            output={output}
            completed={completed[exchange.key]}
            view={view}
            preSx={preSx}
          />
        ))}
      </Box>
      {otherHttp.length > 0 && (
        <>
          <Label>Other API calls</Label>
          <HttpCallsByStep http={otherHttp} kind={kind} view={view} preSx={preSx} />
        </>
      )}
      <Rest obj={args} shown={WRITE_INPUT_SHOWN} view={view} preSx={preSx} />
      {output ? (
        <Rest obj={output} shown={WRITE_OUTPUT_SHOWN} view={view} preSx={preSx} />
      ) : (
        <Field label="Output (raw)" value={value} view={view} preSx={preSx} />
      )}
    </>
  );
};
